import { buildIntegrationCurls, buildIntegrationMailText } from '../../../utils/buildIntegrationTexts';
import type { IntegrationRequestDetail } from '../../../../../models/IntegrationRequest';
import type { IntegrationPrepareResult } from '../../../../../models/IntegrationRun';
import { NotificationSeverity } from '@/components/notifications/Notification';
import { buildIntegrationDocData } from '../../../utils/integrationDocData';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import { copyToClipboard } from '@/utils/copyToClipboard';
import { useIntegrationDocDownload, useIsIntegrationIt } from '../../../hooks';
import { GlobalButton } from '@/components';
import { useNotification } from '@/hooks';
import DetailFields from '../DetailFields';
import CopyableText from '../CopyableText';
import DetailCard from '../DetailCard';
import { useMemo } from 'react';

interface IntegrationDeliveryProps {
  request: IntegrationRequestDetail;
  prepare: IntegrationPrepareResult;
  isInReview: boolean;
}

const IntegrationDelivery = ({ request, prepare, isInReview }: IntegrationDeliveryProps) => {
  const isIt = useIsIntegrationIt();
  const { showNotification } = useNotification();
  const { downloadDoc, isDownloading } = useIntegrationDocDownload();
  const data = useMemo(() => buildIntegrationDocData(request, prepare), [request, prepare]);
  const mailText = useMemo(() => buildIntegrationMailText(data), [data]);

  const copy = async (text: string, label: string) => {
    const copied = await copyToClipboard(text);
    showNotification(
      copied ? `${label} copiado` : `No se pudo copiar ${label.toLowerCase()}`,
      copied ? NotificationSeverity.success : NotificationSeverity.error
    );
  };

  return (
    <DetailCard
      title='Entrega al cliente'
      action={
        <div className='flex flex-wrap gap-2'>
          {isIt && (
            <GlobalButton
              variant={GlobalButtonVariant.secondaryButton}
              action={() => copy(buildIntegrationCurls(data), 'Curls')}
            >
              Copiar curls
            </GlobalButton>
          )}
          <GlobalButton
            variant={GlobalButtonVariant.secondaryButton}
            action={() => downloadDoc(data)}
            disabled={isDownloading}
          >
            Descargar documentación
          </GlobalButton>
        </div>
      }
    >
      <p className='text-sm text-gray-600'>
        {isInReview
          ? 'Revise la documentación y apruébela. Hasta que la apruebe, comercial no ve las credenciales ni puede descargarla.'
          : 'Descargue la documentación en PDF y adjúntela al mail. Cuando lo envíe, marque la solicitud como enviada al cliente.'}
      </p>
      <DetailFields
        items={[
          { label: 'Email de integración', value: data.email },
          { label: 'Password', value: data.password },
          { label: 'API Key', value: data.apiKey },
          { label: 'API Secret', value: data.apiSecret },
          { label: 'Commerce Code', value: data.commerceCode },
          { label: 'Logistic Operator Code', value: data.operatorCode },
          ...(data.testApiBase ? [{ label: 'URL test', value: data.testApiBase }] : []),
          { label: 'URL producción', value: data.prodApiBase },
        ]}
      />
      <CopyableText
        text={mailText}
        copyLabel='Copiar texto del mail'
        onCopy={() => copy(mailText, 'Texto del mail')}
      />
    </DetailCard>
  );
};

export default IntegrationDelivery;
